import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Check, Loader2, Sparkles } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { createCheckoutSession, getSubscription } from '@/services/subscription'
import { Skeleton } from '@/components/ui/skeleton'

const priceId = import.meta.env.VITE_STRIPE_PRICE_ID

export default function SubscribePage() {
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false)
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true)
  const { toast } = useToast()
  const navigate = useNavigate()

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const subscription = await getSubscription()
        if (subscription) {
          navigate('/dashboard', { replace: true })
        } else {
          setIsCheckingSubscription(false)
        }
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Erro',
          description: 'Não foi possível verificar sua assinatura.',
        })
        setIsCheckingSubscription(false)
      }
    }
    checkSubscription()
  }, [navigate, toast])

  const handleSubscribe = async () => {
    setIsCheckoutLoading(true)
    try {
      const { url } = await createCheckoutSession(priceId)
      if (url) {
        window.location.href = url
      }
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar assinatura',
        description: error.message || 'Por favor, tente novamente.',
      })
      setIsCheckoutLoading(false)
    }
  }

  if (isCheckingSubscription) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-72px)] p-4">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="h-8 w-3/4 mx-auto" />
          <Skeleton className="h-6 w-1/2 mx-auto" />
          <Card className="w-full">
            <CardHeader>
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-4 w-2/3" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-10 w-1/2 mx-auto" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </CardContent>
            <CardFooter>
              <Skeleton className="h-12 w-full" />
            </CardFooter>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-72px)] bg-background p-4">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold tracking-tight">
          Desbloqueie o Briefly
        </h1>
        <p className="mt-3 max-w-2xl mx-auto text-lg text-muted-foreground">
          Comece com 7 dias grátis e simplifique sua leitura diária.
        </p>
      </div>

      <Card className="w-full max-w-md shadow-lg border-primary/50 animate-fade-in-up">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Plano Mensal</CardTitle>
          <CardDescription>
            Acesso ilimitado a todos os recursos premium.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <span className="text-4xl font-bold">R$39</span>
            <span className="text-xl text-muted-foreground">/mês</span>
          </div>
          <div className="text-center font-semibold text-green-400">
            <p>Comece com 7 dias grátis!</p>
          </div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center">
              <Check className="mr-2 h-4 w-4 text-green-500" />
              Até 10 Feeds RSS
            </li>
            <li className="flex items-center">
              <Check className="mr-2 h-4 w-4 text-green-500" />
              Até 5 E-mails destinatários
            </li>
            <li className="flex items-center">
              <Check className="mr-2 h-4 w-4 text-green-500" />
              Tópicos Prioritários
            </li>
            <li className="flex items-center">
              <Check className="mr-2 h-4 w-4 text-green-500" />
              Envio Diário Automatizado
            </li>
          </ul>
        </CardContent>
        <CardFooter>
          <Button
            className="w-full"
            size="lg"
            onClick={handleSubscribe}
            disabled={isCheckoutLoading}
          >
            {isCheckoutLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Iniciar teste gratuito
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
